/**
 * _variants.ts — Fábrica de variantes demo por categoría.
 *
 * QUÉ HACE:
 *   Recibe (categoryId, productId, index) y devuelve un par
 *   `{ variantes, variantesSKUs }` listo para inyectar en un Producto demo,
 *   según reglas específicas de cada categoría (Calzado, Ropa, Joyería,
 *   Tecnología, Bebidas, Accesorios, Belleza).
 *
 * QUÉ EXPORTA:
 *   - inferVariantesPorCategoria(categoryId, productId, index, stockBase)
 *
 * DEPENDE DE:
 *   - types (VariantGroup, VariantOption, VariantSKU)
 *   - _factory.pickCategoryImage para swatches de color
 *
 * REGLAS:
 *   - Tipo UI por grupo lo decide esta fábrica (simula la decisión del negocio).
 *   - `precioExtra` siempre es monto fijo positivo (o 0).
 *   - Solo se generan hasta 3 filas de `variantesSKUs` de ejemplo (disperso); el
 *     resto de combinaciones las agrega el usuario en inventario.
 *   - El stock demo se reparte solo entre esas muestras.
 *   - Si la categoría no tiene reglas, se retorna { variantes: [], skus: [] }.
 *
 * QUÉ NO HACE:
 *   - No hace fetch ni IO.
 *   - No mutan producto; solo computa datos puros.
 */
import type { VariantGroup, VariantOption, VariantSKU } from "@/types";
import { pickCategoryImage } from "./_factory";

export interface VariantBundle {
  variantes: VariantGroup[];
  variantesSKUs: VariantSKU[];
}

// ──────────────────────────────────────────── utils ────────────────────────────────────────────
const seedFromStr = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
};

/** Producto cartesiano de varias listas de opciones (una por grupo). */
const cartesian = <T>(lists: T[][]): T[][] => {
  if (lists.length === 0) return [[]];
  return lists.reduce<T[][]>(
    (acc, list) => acc.flatMap((prev) => list.map((x) => [...prev, x])),
    [[]],
  );
};

/** Reparte `stockBase` entre `combos.length` sumando al menos 1 por SKU. */
const distribuirStock = (stockBase: number, combosLen: number): number[] => {
  const base = Math.max(1, Math.floor(stockBase / Math.max(1, combosLen)));
  const sobrante = Math.max(0, stockBase - base * combosLen);
  const stocks = Array.from({ length: combosLen }, () => base);
  for (let i = 0; i < sobrante; i++) stocks[i] += 1;
  return stocks;
};

/** Cuántas filas SKU demo: primera, central y última combinación del cartesiano. */
const MUESTRA_SKU_DEMO = 3;

const elegirCombosDemo = <T>(combos: T[][]): T[][] => {
  if (combos.length === 0) return [];
  if (combos.length <= MUESTRA_SKU_DEMO) return combos;
  return [
    combos[0],
    combos[Math.floor(combos.length / 2)],
    combos[combos.length - 1],
  ];
};

// ──────────────────────────────────────── Builders por categoría ───────────────────────────────
const buildCalzado = (productId: string): VariantBundle => {
  const tallaOpciones: VariantOption[] = [
    { id: "op_t35", label: "35" },
    { id: "op_t36", label: "36" },
    { id: "op_t37", label: "37" },
    { id: "op_t38", label: "38" },
    { id: "op_t39", label: "39" },
    { id: "op_t40", label: "40" },
    { id: "op_t41", label: "41" },
    { id: "op_t42", label: "42" },
  ];
  const colores: { id: string; label: string; hex: string }[] = [
    { id: "op_neg", label: "Negro", hex: "#111827" },
    { id: "op_bla", label: "Blanco", hex: "#f8fafc" },
    { id: "op_azu", label: "Azul", hex: "#1d4ed8" },
    { id: "op_roj", label: "Rojo", hex: "#dc2626" },
  ];
  const colorOpciones: VariantOption[] = colores.map((c, i) => ({
    id: c.id,
    label: c.label,
    colorHex: c.hex,
    imagen: pickCategoryImage("cat_calz", productId, i + 3) ?? undefined,
  }));
  return buildBundle(productId, [
    {
      id: "grp_talla",
      kind: "talla",
      label: "Talla",
      ui: "buttons",
      required: true,
      opciones: tallaOpciones,
    },
    {
      id: "grp_color",
      kind: "color",
      label: "Color",
      ui: "swatches",
      required: true,
      opciones: colorOpciones,
    },
  ]);
};

const buildRopa = (productId: string): VariantBundle => {
  const tallas: VariantOption[] = [
    { id: "op_xs", label: "XS" },
    { id: "op_s", label: "S" },
    { id: "op_m", label: "M" },
    { id: "op_l", label: "L" },
    { id: "op_xl", label: "XL", precioExtra: 5000 },
    { id: "op_xxl", label: "XXL", precioExtra: 10000 },
  ];
  const colores: { id: string; label: string; hex: string }[] = [
    { id: "op_neg", label: "Negro", hex: "#0f172a" },
    { id: "op_bla", label: "Blanco", hex: "#f8fafc" },
    { id: "op_gri", label: "Gris", hex: "#64748b" },
    { id: "op_bei", label: "Beige", hex: "#d6b98a" },
  ];
  const colorOpciones: VariantOption[] = colores.map((c, i) => ({
    id: c.id,
    label: c.label,
    colorHex: c.hex,
    imagen: pickCategoryImage("cat_ropa", productId, i + 3) ?? undefined,
  }));
  return buildBundle(productId, [
    {
      id: "grp_talla",
      kind: "talla",
      label: "Talla",
      ui: "buttons",
      required: true,
      opciones: tallas,
    },
    {
      id: "grp_color",
      kind: "color",
      label: "Color",
      ui: "swatches",
      required: true,
      opciones: colorOpciones,
    },
  ]);
};

const buildJoyeria = (productId: string): VariantBundle => {
  const material: VariantOption[] = [
    { id: "op_pla", label: "Plata 925" },
    { id: "op_oro", label: "Oro 18k", precioExtra: 500000 },
    { id: "op_ace", label: "Acero inoxidable", precioExtra: 0 },
  ];
  const tallaAnillo: VariantOption[] = [
    { id: "op_5", label: "5" },
    { id: "op_6", label: "6" },
    { id: "op_7", label: "7" },
    { id: "op_8", label: "8" },
    { id: "op_9", label: "9" },
  ];
  return buildBundle(productId, [
    {
      id: "grp_mat",
      kind: "material",
      label: "Material",
      ui: "buttons",
      required: true,
      opciones: material,
    },
    {
      id: "grp_talla",
      kind: "talla",
      label: "Talla (anillo)",
      ui: "dropdown",
      required: false,
      opciones: tallaAnillo,
    },
  ]);
};

const buildTecnologia = (productId: string): VariantBundle => {
  const capacidad: VariantOption[] = [
    { id: "op_128", label: "128 GB" },
    { id: "op_256", label: "256 GB", precioExtra: 300000 },
    { id: "op_512", label: "512 GB", precioExtra: 600000 },
  ];
  const colores: { id: string; label: string; hex: string }[] = [
    { id: "op_mn", label: "Medianoche", hex: "#111827" },
    { id: "op_pt", label: "Platino", hex: "#e5e7eb" },
    { id: "op_gr", label: "Grafito", hex: "#374151" },
  ];
  const colorOpciones: VariantOption[] = colores.map((c, i) => ({
    id: c.id,
    label: c.label,
    colorHex: c.hex,
    imagen: pickCategoryImage("cat_tech", productId, i + 3) ?? undefined,
  }));
  return buildBundle(productId, [
    {
      id: "grp_cap",
      kind: "capacidad",
      label: "Capacidad",
      ui: "buttons",
      required: true,
      opciones: capacidad,
    },
    {
      id: "grp_color",
      kind: "color",
      label: "Color",
      ui: "swatches",
      required: true,
      opciones: colorOpciones,
    },
  ]);
};

const buildBebidas = (productId: string): VariantBundle => {
  const presentacion: VariantOption[] = [
    { id: "op_350", label: "350 ml" },
    { id: "op_500", label: "500 ml", precioExtra: 1000 },
    { id: "op_1l", label: "1 Litro", precioExtra: 2500 },
  ];
  return buildBundle(productId, [
    {
      id: "grp_pres",
      kind: "capacidad",
      label: "Presentación",
      ui: "dropdown",
      required: true,
      opciones: presentacion,
    },
  ]);
};

const buildAccesorios = (productId: string): VariantBundle => {
  const colores: { id: string; label: string; hex: string }[] = [
    { id: "op_neg", label: "Negro", hex: "#0f172a" },
    { id: "op_mar", label: "Marrón", hex: "#78350f" },
    { id: "op_bei", label: "Beige", hex: "#d6b98a" },
    { id: "op_rsd", label: "Rosa seco", hex: "#f4a7a7" },
  ];
  const colorOpciones: VariantOption[] = colores.map((c, i) => ({
    id: c.id,
    label: c.label,
    colorHex: c.hex,
    imagen: pickCategoryImage("cat_acc", productId, i + 3) ?? undefined,
  }));
  return buildBundle(productId, [
    {
      id: "grp_color",
      kind: "color",
      label: "Color",
      ui: "swatches",
      required: true,
      opciones: colorOpciones,
    },
  ]);
};

const buildBelleza = (productId: string): VariantBundle => {
  const tonos: VariantOption[] = [
    { id: "op_nude", label: "Nude", colorHex: "#e0b294" },
    { id: "op_coral", label: "Coral", colorHex: "#ff7f50" },
    { id: "op_rosa", label: "Rosa", colorHex: "#ff6b9d" },
    { id: "op_vino", label: "Vino", colorHex: "#722f37" },
    { id: "op_rojo", label: "Rojo clásico", colorHex: "#c1121f" },
  ];
  return buildBundle(productId, [
    {
      id: "grp_tono",
      kind: "color",
      label: "Tono",
      ui: "swatches",
      required: true,
      opciones: tonos,
    },
  ]);
};

// ─────────────────────────────────────── builder genérico SKU ───────────────────────────────────
/** Genera bundle (variantes + SKUs con stock repartido) a partir de los grupos. */
const buildBundle = (
  productId: string,
  grupos: VariantGroup[],
): VariantBundle => {
  const listas = grupos.map((g) => g.opciones);
  const combos = cartesian(listas);
  const combosDemo = elegirCombosDemo(combos);
  // Stock base ficticio repartido solo entre las pocas filas demo.
  const stockBase = 30 + (seedFromStr(productId) % 40); // 30..69
  const stocks = distribuirStock(stockBase, combosDemo.length);
  const skus: VariantSKU[] = combosDemo.map((combo, i) => {
    const combinacion: Record<string, string> = {};
    combo.forEach((opt, gi) => {
      combinacion[grupos[gi].id] = opt.id;
    });
    return { combinacion, stock: stocks[i] };
  });
  return { variantes: grupos, variantesSKUs: skus };
};

// ─────────────────────────────────────── API pública ────────────────────────────────────────────
const BUILDERS: Record<string, (productId: string) => VariantBundle> = {
  cat_calz: buildCalzado,
  cat_ropa: buildRopa,
  cat_joy: buildJoyeria,
  cat_tech: buildTecnologia,
  cat_beb: buildBebidas,
  cat_acc: buildAccesorios,
  cat_bell: buildBelleza,
};

/**
 * Retorna variantes+SKUs demo para la categoría indicada, o null si no hay reglas.
 */
export const inferVariantesPorCategoria = (
  categoryId: string,
  productId: string,
): VariantBundle | null => {
  const fn = BUILDERS[categoryId];
  if (!fn) return null;
  return fn(productId);
};
